import React, { useContext } from 'react'

import { Icon, IconButton } from '@mui/material'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { useAuth } from 'context/auth'
import { DBActionType, SubOrganisationsSchema } from 'context/materialUI/types'

import { FormContext } from '../../constants'

function ManageOrganisations() {
	const formContext = useContext(FormContext)
	const { localForm, setLocalForm, setFormEdited } = formContext
	const { user } = useAuth()

	const subOrganisations = localForm?.subOrganisations
	const defaultOrg = user.data.organisation.name

	const handleFormUpdate = (value: SubOrganisationsSchema[]) => {
		setLocalForm(prev => ({
			...prev,
			subOrganisations: value,
		}))
		setFormEdited(true)
	}

	return (
		<Card id="organisations">
			<MDBox p={2}>
				<Grid container spacing={3} alignItems="center">
					<Grid item xs={12}>
						<MDBox display={'flex'} flexDirection={'column'} gap={2}>
							<MDTypography variant="h5" fontWeight="medium">
								Sub Organisations
							</MDTypography>
							<MDTypography variant="button" fontWeight="regular" color="text">
								<i>
									Please detail the different organisations that you manage here that you would like
									to track. This will be used to create the possible organisations to attach to
									members. Which can then be used for sorting and filtering.
								</i>
							</MDTypography>
						</MDBox>
					</Grid>
					<Grid item xs={12}>
						<Grid item xs={12}>
							<MDBox mb={2}>
								<MDTypography variant="h6" mb={1}>
									Default Organisation:
								</MDTypography>
								<MDTypography variant="body2" color="gray500">
									<i>{defaultOrg}</i>
								</MDTypography>
							</MDBox>
							{subOrganisations?.map((item, index) => {
								return (
									<MDBox
										key={index}
										display={'flex'}
										flexDirection={'row'}
										gap={4}
										alignItems={'center'}
										justifyContent={'space-between'}
										mr={2}
										my={2}
									>
										<MDInput
											variant="outlined"
											value={item.newValue?.find(nv => nv.field === 'name')?.value ?? item.name}
											size="medium"
											label="Organisation"
											autoComplete="off"
											InputLabelProps={{ shrink: true }}
											sx={{ width: '25rem' }}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
												const userInput = e.target.value
												const updatedSubOrgs = subOrganisations.map((entry, i) => {
													const itemIndex = subOrganisations.indexOf(item)
													if (i !== itemIndex) return entry
													const isUpdate = entry.dbAction === 'UPDATE' || !entry.dbAction

													return {
														...entry,
														dbAction: isUpdate
															? ('UPDATE' as DBActionType)
															: ('CREATE' as DBActionType),
														// Keep original outcome untouched if it's an update
														newValue: isUpdate
															? [
																	{
																		field: 'name',
																		value: userInput,
																	},
																]
															: undefined,
														// Only set outcome directly for new items (CREATE)
														name: isUpdate ? entry.name : userInput,
													}
												})
												handleFormUpdate(updatedSubOrgs)
											}}
										/>

										<IconButton
											size="medium"
											aria-label="delete"
											color="error"
											onClick={() => {
												const index = subOrganisations.indexOf(item)
												const temp = [...subOrganisations] // Create a shallow copy

												if (item.dbAction === 'CREATE') {
													temp.splice(index, 1) // Remove the item
												} else {
													temp[index] = { ...item, dbAction: 'DELETE', newValue: undefined } //mark it for deletion
												}
												handleFormUpdate(temp)
											}}
										>
											<Icon fontSize="medium">delete_outline</Icon>
										</IconButton>
									</MDBox>
								)
							})}
						</Grid>
					</Grid>
				</Grid>
				<MDButton
					color="primary"
					variant="outlined"
					onClick={() => {
						handleFormUpdate([
							...subOrganisations,
							{ name: '', status: 'ACTIVE', dbAction: 'CREATE' },
						])
					}}
				>
					+ Add New Organisation
				</MDButton>
			</MDBox>
		</Card>
	)
}

export default ManageOrganisations
